import { observer } from 'mobx-react-lite'
import React from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { DS } from '../../../../design-system'
import { useStore } from '../../../../models'
import { fontFamily } from '../../../../theme'
import { palette } from '../../../../theme/palette'
import { Card } from '../../Card'
import { WalkthroughOverlayActions } from '../WalkthroughOverlayActions'
import { useIsEmbeddedSegment } from '@/hooks/useIsEmbeddedSegment'

const isWeb = Platform.OS === 'web'

export const RaiseOrFold = observer(() => {
  const {
    game: { isFirstStep, isLastStep },
    session: { selectedDailyTask, updateDailyTaskStatus }
  } = useStore()

  const markRaiseOrFoldTaskAsComplete = React.useCallback(async () => {
    try {
      if (selectedDailyTask) await updateDailyTaskStatus()
    } catch (error) {
      console.log('markRaiseOrFoldTaskAsComplete error', error)
    }
  }, [selectedDailyTask, updateDailyTaskStatus])

  React.useEffect(() => {
    return () => {
      markRaiseOrFoldTaskAsComplete()
    }
  }, [markRaiseOrFoldTaskAsComplete])

  const Content = isLastStep ? RaiseOrFoldFinalScreen : isFirstStep ? RaiseOrFoldFirstScreen : null

  if (!Content) return null

  return (
    <SafeAreaView>
      <View style={styles.container}>
        <Content />
      </View>
    </SafeAreaView>
  )
})

const RaiseOrFoldFirstScreen = () => {
  return (
    <>
      <View style={styles.cardsBackground}>
        <View style={styles.leftGroupOfCards}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ marginRight: 10 }}>
              <View style={{ marginBottom: 5 }}>
                <Card fontSize={40} shadow rotateDegrees="-5deg" />
              </View>
              <View style={{ left: 10 }}>
                <Card fontSize={40} shadow rotateDegrees="-10deg" />
              </View>
            </View>
            <View style={{ bottom: 10 }}>
              <Card fontSize={40} shadow rotateDegrees="-5deg" />
            </View>
          </View>
        </View>
        <View style={styles.rightGroupOfCards}>
          <View style={{ bottom: 15 }}>
            <Card fontSize={40} shadow rotateDegrees="10deg" />
          </View>
          <View style={{ marginLeft: 15 }}>
            <View style={{ marginBottom: 5 }}>
              <Card fontSize={40} shadow rotateDegrees="5deg" />
            </View>
            <View style={{ right: 10 }}>
              <Card fontSize={40} shadow rotateDegrees="5deg" />
            </View>
          </View>
        </View>
      </View>

      <View style={[styles.content, isWeb ? { paddingHorizontal: 150 } : undefined]}>
        <View style={styles.cardsContainer}>
          <Card card="Th" fontSize={30} rotateDegrees="6deg" shadow />
          <Card card="Ad" fontSize={30} rotateDegrees="-6deg" shadow />
        </View>
        <DS.Text variant="Heading 1/Medium" textAlign="center" color="Global/White">
          Raise or Fold
        </DS.Text>
        <DS.Text variant="Body Large/Medium" textAlign="center" color="Global/White">
          Test your instincts with hole cards.
        </DS.Text>
        <DS.Text variant="Body Medium/Regular" textAlign="center" color="Global/White">
          Here you'll evaluate each person's hole cards and decide whether they should raise or fold. Let's see if you
          make the right call.
        </DS.Text>
      </View>
      <View style={styles.walkthroughOverlayActions}>
        <WalkthroughOverlayActions />
      </View>
    </>
  )
}

const RaiseOrFoldFinalScreen = () => {
  const isEmbedded = useIsEmbeddedSegment()
  return (
    <>
      <View style={styles.content}>
        <View style={styles.cardsContainer}>
          <Card card="Kc" fontSize={30} rotateDegrees="6deg" shadow />
          <Card card="Ad" fontSize={30} rotateDegrees="-6deg" shadow />
        </View>
        <DS.Text variant="Heading 2/Medium" textAlign="center" color="Global/White">
          Play again {isEmbedded ? '' : 'or continue walkthrough'}?
        </DS.Text>
      </View>
      <WalkthroughOverlayActions />
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%'
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    gap: 15,
    padding: 30
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: -127
  },
  text: {
    color: palette.white,
    textAlign: 'center'
  },
  title: {
    fontFamily: fontFamily.FeatureDeckRegular,
    fontSize: 32
  },
  subtitle: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 18
  },
  body: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    fontSize: 16
  },
  walkthroughOverlayActions: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardsBackground: {
    flexDirection: 'row',
    width: '100%',
    position: 'absolute',
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'space-between',
    top: 10
  },
  leftGroupOfCards: {
    flexDirection: 'row',
    alignItems: 'center',
    left: -35,
    top: 40
  },
  rightGroupOfCards: {
    flexDirection: 'row',
    alignItems: 'center',
    right: -35
  }
})
